/**
 * Where the audio bytes live.
 *
 * Two backends behind one API:
 *
 * - **OPFS** (`navigator.storage.getDirectory`) when the browser can stream into it. Writes
 *   go through `createWritable`, a chunk at a time, so importing a 200 MB file costs one
 *   chunk of memory rather than 200 MB. Reads hand back a real `File`, which means an
 *   `<audio>` element or `decodeAudioData` can be pointed straight at it.
 * - **IndexedDB blob chunks** everywhere else. The bytes are stored as a series of `Blob`
 *   records rather than one giant one: a `Blob` slice does not copy, so both writing and
 *   reading stay bounded, and — this is the important part — every chunk commits in its own
 *   transaction, so a tab that dies mid-write leaves a shorter file rather than no file.
 *
 * That last property is why the IndexedDB path is not merely a fallback. Recording asks for
 * it explicitly (`durable: true`) even when OPFS is available, because `createWritable`
 * accumulates into a swap file that is only swapped in on `close()`: perfect for an atomic
 * import, useless for a take the user is halfway through when iOS kills the tab. Each
 * object's index row records which backend holds it, so reads dispatch correctly no matter
 * which path wrote it.
 *
 * Feature detection is a real write, not a `typeof` check. A `FileSystemFileHandle` can
 * exist in contexts where writing to it throws, and finding that out during a user's first
 * import is not acceptable.
 */

import { createLogger, bytes as byteTag, idPrefix } from '../core/log';
import {
  StorageError,
  databaseBackend,
  isQuotaError,
  keyRange,
  openDatabase,
  readStore,
  transact,
  writeStore,
  type BlobBackendName,
  type BlobRecord,
} from './db';
import { nowISODate } from '../model/types';

const log = createLogger('blobs');

/**
 * 4 MB per IndexedDB record. Large enough that a 200 MB file is 50 records rather than
 * 50,000, small enough that a failed write loses a fraction of a second of a recording.
 */
const CHUNK_BYTES = 4 * 1024 * 1024;

/** Directory inside the origin-private file system. */
const OPFS_DIRECTORY = 'media';

const PROBE_NAME = '.padlab-write-probe';

/**
 * How recently an unindexed file must have been touched to be left alone by `reconcile()`.
 * Two minutes is longer than any single write this app performs and far shorter than the
 * gap between one launch and the next, so a genuine orphan is always collected eventually.
 */
const ORPHAN_GRACE_MS = 120_000;

/**
 * Ids become OPFS file names, so they are held to file-name rules: no separators, no dot
 * segments, no leading dot, and short enough for every file system PadLab might sit on.
 */
const VALID_ID = /^[A-Za-z0-9][A-Za-z0-9._-]{0,190}$/;

function assertValidID(id: string): void {
  if (!VALID_ID.test(id) || id.includes('..')) {
    throw new StorageError(
      'corrupt',
      'That sound has an identifier PadLab cannot store.',
      'Re-import the sound. If it came from a project package, the package may have been edited by hand.',
    );
  }
}

// MARK: - Minimal OPFS surface
//
// Declared here rather than relying on lib.dom: the File System Access types move between
// TypeScript releases and between the DOM and worker libs, and this module only needs five
// members. Everything is optional because every one of them is genuinely missing somewhere.

interface WritableFileStream {
  write(data: ArrayBuffer | ArrayBufferView | Blob | string): Promise<void>;
  close(): Promise<void>;
  abort?(reason?: unknown): Promise<void>;
}

interface OPFSFileHandle {
  getFile(): Promise<File>;
  createWritable?(options?: { keepExistingData?: boolean }): Promise<WritableFileStream>;
}

interface OPFSDirectoryHandle {
  getFileHandle(name: string, options?: { create?: boolean }): Promise<OPFSFileHandle>;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<OPFSDirectoryHandle>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  keys?(): AsyncIterableIterator<string>;
}

interface StorageManagerWithOPFS {
  getDirectory?: () => Promise<OPFSDirectoryHandle>;
}

// MARK: - Public shape

export interface BlobStat {
  id: string;
  byteSize: number;
  mimeType: string;
  backend: BlobBackendName;
}

export interface BlobWriteOptions {
  mimeType?: string;
  /**
   * Commit every chunk as it arrives instead of on close. Costs a little throughput and buys
   * a recording that survives the tab being killed.
   */
  durable?: boolean;
  /** Total bytes, when known, so the writer can check for space before it starts. */
  expectedBytes?: number;
}

export interface BlobWriter {
  readonly id: string;
  readonly bytesWritten: number;
  write(chunk: Uint8Array | ArrayBuffer | Blob): Promise<void>;
  close(): Promise<BlobStat>;
  /** Discards everything written so far. Safe to call twice. */
  abort(): Promise<void>;
}

export type BlobSource = Blob | ArrayBuffer | Uint8Array | ReadableStream<Uint8Array>;

export interface BlobPutOptions extends BlobWriteOptions {
  onProgress?: (written: number, total: number | undefined) => void;
  signal?: AbortSignal;
}

export interface BlobStore {
  /** The backend new writes go to. Reads follow whatever wrote each object. */
  readonly backend: BlobBackendName;
  /** One sentence for the storage screen and the capability matrix. */
  describeBackend(): string;

  put(id: string, source: BlobSource, options?: BlobPutOptions): Promise<BlobStat>;
  createWriter(id: string, options?: BlobWriteOptions): Promise<BlobWriter>;

  /** Throws when the object is missing — the caller is about to play or export it. */
  get(id: string): Promise<Blob>;
  tryGet(id: string): Promise<Blob | undefined>;
  /**
   * A URL for `<audio src>` or `decodeAudioData`. Reference counted: every call must be
   * paired with `releaseFileURL(id)`, and everything is revoked when the page really goes
   * away (not on a back-forward-cache suspend, where the URLs are still live on restore).
   */
  getFileURL(id: string): Promise<string>;
  releaseFileURL(id: string): void;
  releaseAllFileURLs(): void;

  delete(id: string): Promise<void>;
  /** Removes every object whose id starts with `prefix` — how a project's media is dropped. */
  deleteMatching(prefix: string): Promise<number>;

  has(id: string): Promise<boolean>;
  size(id: string): Promise<number>;
  list(prefix?: string): Promise<BlobStat[]>;
  totalBytes(prefix?: string): Promise<number>;
  copy(fromID: string, toID: string): Promise<BlobStat>;

  /**
   * Reconciles the index against what is really stored: repairs sizes, drops rows whose
   * bytes vanished, and deletes bytes no row owns. Returns the bytes reclaimed.
   */
  reconcile(): Promise<number>;
}

// MARK: - Backend detection

let opfsDirectory: OPFSDirectoryHandle | null = null;
let storePromise: Promise<BlobStore> | null = null;

async function detectOPFS(): Promise<OPFSDirectoryHandle | null> {
  const storage = navigator.storage as unknown as StorageManagerWithOPFS | undefined;
  if (storage === undefined || typeof storage.getDirectory !== 'function') return null;

  try {
    const root = await storage.getDirectory();
    const directory = await root.getDirectoryHandle(OPFS_DIRECTORY, { create: true });
    const probe = await directory.getFileHandle(PROBE_NAME, { create: true });
    if (typeof probe.createWritable !== 'function') {
      // OPFS without `createWritable` (WebKit before Safari 17) can only be written from a
      // worker through a sync access handle. Rather than run every import through a second
      // worker hop, those browsers get the IndexedDB path, which is fully supported there.
      await directory.removeEntry(PROBE_NAME).catch(() => undefined);
      return null;
    }
    const writable = await probe.createWritable();
    await writable.write(new Uint8Array([0x50, 0x4c]));
    await writable.close();
    const size = (await probe.getFile()).size;
    await directory.removeEntry(PROBE_NAME).catch(() => undefined);
    return size === 2 ? directory : null;
  } catch (cause) {
    log.warn('origin-private file system unavailable; using IndexedDB for audio', {
      reason: idPrefix(cause instanceof Error ? cause.name : 'unknown'),
    });
    return null;
  }
}

// MARK: - Index bookkeeping

async function readIndex(id: string): Promise<BlobRecord | undefined> {
  return readStore('blobs', (store) => store.get(id));
}

async function writeIndex(record: BlobRecord): Promise<void> {
  await writeStore('blobs', (store) => store.put(record));
}

// MARK: - Object URLs

interface TrackedURL {
  url: string;
  refs: number;
}

const objectURLs = new Map<string, TrackedURL>();
let teardownInstalled = false;

function installTeardown(): void {
  if (teardownInstalled || typeof window === 'undefined') return;
  teardownInstalled = true;
  // `pagehide` with `persisted === true` is a back-forward-cache suspend: the document comes
  // back with its URLs still valid, so revoking there would break a restore. Only a real
  // teardown reclaims them. `unload` is never used — it disables the cache outright.
  window.addEventListener('pagehide', (event) => {
    if (!event.persisted) revokeAll();
  });
}

function revokeAll(): void {
  for (const tracked of objectURLs.values()) URL.revokeObjectURL(tracked.url);
  objectURLs.clear();
}

// MARK: - Chunk helpers

function chunkByteLength(chunk: Uint8Array | ArrayBuffer | Blob): number {
  if (chunk instanceof Blob) return chunk.size;
  if (chunk instanceof ArrayBuffer) return chunk.byteLength;
  return chunk.byteLength;
}

// MARK: - IndexedDB writer

class IndexedDBWriter implements BlobWriter {
  readonly id: string;
  bytesWritten = 0;

  private readonly mimeType: string;
  private readonly durable: boolean;
  private readonly release: () => void;
  private pending: Array<Uint8Array | Blob> = [];
  private pendingBytes = 0;
  private chunkIndex = 0;
  private closed = false;

  constructor(id: string, mimeType: string, durable: boolean, release: () => void) {
    this.id = id;
    this.mimeType = mimeType;
    this.durable = durable;
    this.release = release;
  }

  async write(chunk: Uint8Array | ArrayBuffer | Blob): Promise<void> {
    if (this.closed) throw StorageError.aborted();
    const length = chunkByteLength(chunk);
    if (length === 0) return;

    this.pending.push(chunk instanceof ArrayBuffer ? new Uint8Array(chunk) : chunk);
    this.pendingBytes += length;
    this.bytesWritten += length;

    // A durable writer flushes whatever it has: a recording chunk that sits in memory
    // waiting for 4 MB of company is a chunk the user loses when the tab is discarded.
    if (this.durable || this.pendingBytes >= CHUNK_BYTES) await this.flush();
  }

  async close(): Promise<BlobStat> {
    if (this.closed) throw StorageError.aborted();
    await this.flush();
    this.closed = true;
    try {
      await this.writeIndexRow();
    } finally {
      this.release();
    }
    return { id: this.id, byteSize: this.bytesWritten, mimeType: this.mimeType, backend: 'indexeddb' };
  }

  async abort(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    this.pending = [];
    this.pendingBytes = 0;
    try {
      await deleteIndexedDBObject(this.id);
    } finally {
      this.release();
    }
  }

  private async flush(): Promise<void> {
    if (this.pending.length === 0) return;
    const parts = this.pending;
    this.pending = [];
    this.pendingBytes = 0;

    const index = this.chunkIndex;
    this.chunkIndex += 1;
    // Every part came in through `write`, whose signature accepts any typed-array backing;
    // `Blob` only names the plain-`ArrayBuffer` case in its own types.
    const data = new Blob(parts as BlobPart[], { type: this.mimeType });

    try {
      await transact(['blobChunks', 'blobs'], 'readwrite', async (tx) => {
        await tx.store('blobChunks').put({ id: this.id, index, data });
        // The index row is updated in the same transaction, so what the row claims and what
        // is stored can never disagree — including after a crash.
        await tx.store('blobs').put(this.indexRow());
      });
    } catch (cause) {
      if (isQuotaError(cause)) throw StorageError.quotaExceeded(this.bytesWritten, undefined, cause);
      throw cause;
    }
  }

  private async writeIndexRow(): Promise<void> {
    await writeIndex(this.indexRow());
  }

  private indexRow(): BlobRecord {
    return {
      id: this.id,
      byteSize: this.bytesWritten,
      mimeType: this.mimeType,
      chunkCount: this.chunkIndex,
      backend: 'indexeddb',
      updatedAt: nowISODate(),
    };
  }
}

async function deleteIndexedDBObject(id: string): Promise<void> {
  await transact(['blobChunks', 'blobs'], 'readwrite', async (tx) => {
    await tx.store('blobChunks').delete(keyRange.compoundPrefix(id));
    await tx.store('blobs').delete(id);
  });
}

async function readIndexedDBObject(id: string, record: BlobRecord): Promise<Blob | undefined> {
  const chunks = await readStore('blobChunks', (store) => store.getAll(keyRange.compoundPrefix(id)));
  if (chunks.length === 0) return record.byteSize === 0 ? new Blob([], { type: record.mimeType }) : undefined;
  chunks.sort((left, right) => left.index - right.index);
  return new Blob(
    chunks.map((chunk) => chunk.data),
    { type: record.mimeType },
  );
}

// MARK: - OPFS writer

class OPFSWriter implements BlobWriter {
  readonly id: string;
  bytesWritten = 0;

  private readonly mimeType: string;
  private readonly directory: OPFSDirectoryHandle;
  private readonly writable: WritableFileStream;
  private readonly release: () => void;
  private closed = false;

  constructor(
    id: string,
    mimeType: string,
    directory: OPFSDirectoryHandle,
    writable: WritableFileStream,
    release: () => void,
  ) {
    this.id = id;
    this.mimeType = mimeType;
    this.directory = directory;
    this.writable = writable;
    this.release = release;
  }

  async write(chunk: Uint8Array | ArrayBuffer | Blob): Promise<void> {
    if (this.closed) throw StorageError.aborted();
    const length = chunkByteLength(chunk);
    if (length === 0) return;
    try {
      await this.writable.write(chunk);
    } catch (cause) {
      await this.abort();
      if (isQuotaError(cause)) throw StorageError.quotaExceeded(this.bytesWritten + length, undefined, cause);
      throw cause;
    }
    this.bytesWritten += length;
  }

  async close(): Promise<BlobStat> {
    if (this.closed) throw StorageError.aborted();
    this.closed = true;
    try {
      try {
        await this.writable.close();
      } catch (cause) {
        await this.directory.removeEntry(this.id).catch(() => undefined);
        if (isQuotaError(cause)) throw StorageError.quotaExceeded(this.bytesWritten, undefined, cause);
        throw cause;
      }

      // Trust the file, not the counter: a short write that did not throw would otherwise be
      // recorded as complete.
      const handle = await this.directory.getFileHandle(this.id);
      const byteSize = (await handle.getFile()).size;
      await writeIndex({
        id: this.id,
        byteSize,
        mimeType: this.mimeType,
        chunkCount: 1,
        backend: 'opfs',
        updatedAt: nowISODate(),
      });
      return { id: this.id, byteSize, mimeType: this.mimeType, backend: 'opfs' };
    } finally {
      // Only after the index row exists: releasing earlier would reopen the window in which
      // a concurrent `reconcile()` sees a complete file that nothing claims.
      this.release();
    }
  }

  async abort(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    try {
      if (typeof this.writable.abort === 'function') {
        await this.writable.abort().catch(() => undefined);
      } else {
        await this.writable.close().catch(() => undefined);
      }
      await this.directory.removeEntry(this.id).catch(() => undefined);
      await writeStore('blobs', (store) => store.delete(this.id)).catch(() => undefined);
    } finally {
      this.release();
    }
  }
}

// MARK: - Store

class PadLabBlobStore implements BlobStore {
  readonly backend: BlobBackendName;
  private readonly directory: OPFSDirectoryHandle | null;
  /**
   * Ids with a writer open right now.
   *
   * An OPFS object exists on disk from `createWriter` and only gains its index row in
   * `close()`, so for the whole duration of an import or a take save there is a complete,
   * unindexed file. `reconcile()` reclaims exactly that shape — a file no index row claims —
   * and would delete the original out from under the writer. Since low storage is both when
   * imports are slowest and when people reach for Clear Caches, the two collided precisely
   * where the brief says nothing may be lost. A writer registers here before its first byte
   * and deregisters in `close()`/`abort()`, and the sweep treats these ids as owned.
   */
  private readonly writing = new Set<string>();

  constructor(directory: OPFSDirectoryHandle | null, fallback: BlobBackendName) {
    this.directory = directory;
    this.backend = directory === null ? fallback : 'opfs';
    installTeardown();
  }

  describeBackend(): string {
    switch (this.backend) {
      case 'opfs':
        return 'Sounds are stored in this browser’s private file system, which streams large imports without loading them into memory.';
      case 'indexeddb':
        return 'Sounds are stored in this browser’s database. Everything works; very large imports are a little slower than on browsers with private file storage.';
      case 'memory':
        return 'This browser is not letting PadLab store anything on the device, so sounds are held in memory and will be lost when you close the tab.';
    }
  }

  async createWriter(id: string, options: BlobWriteOptions = {}): Promise<BlobWriter> {
    assertValidID(id);
    const mimeType = options.mimeType ?? 'application/octet-stream';

    // An existing object under this id is replaced, not appended to; leftovers from a failed
    // attempt would otherwise show up on the end of the new file.
    await this.delete(id);

    const release = (): void => {
      this.writing.delete(id);
    };
    this.writing.add(id);
    try {
      const useOPFS = this.directory !== null && options.durable !== true;
      if (useOPFS && this.directory !== null) {
        const handle = await this.directory.getFileHandle(id, { create: true });
        if (typeof handle.createWritable !== 'function') throw StorageError.unavailable();
        const writable = await handle.createWritable({ keepExistingData: false });
        return new OPFSWriter(id, mimeType, this.directory, writable, release);
      }
      return new IndexedDBWriter(id, mimeType, options.durable === true || this.backend === 'memory', release);
    } catch (error) {
      release();
      throw error;
    }
  }

  async put(id: string, source: BlobSource, options: BlobPutOptions = {}): Promise<BlobStat> {
    const mimeType = options.mimeType ?? (source instanceof Blob ? source.type : '');
    const total =
      options.expectedBytes ??
      (source instanceof Blob
        ? source.size
        : source instanceof ArrayBuffer
          ? source.byteLength
          : ArrayBuffer.isView(source)
            ? source.byteLength
            : undefined);

    const writer = await this.createWriter(id, {
      mimeType: mimeType === '' ? 'application/octet-stream' : mimeType,
      ...(options.durable === undefined ? {} : { durable: options.durable }),
    });

    const abort = async (): Promise<never> => {
      await writer.abort();
      throw new DOMException('That import was cancelled.', 'AbortError');
    };
    // Read through a call rather than the property: `signal.aborted` is readonly, so testing
    // it once narrows it to `false` for the rest of the function and every later check would
    // be compiled away — which would make cancellation silently stop working mid-file.
    const cancelled = (): boolean => options.signal?.aborted === true;

    try {
      if (cancelled()) return await abort();

      if (source instanceof Blob) {
        for (let offset = 0; offset < source.size; offset += CHUNK_BYTES) {
          if (cancelled()) return await abort();
          await writer.write(source.slice(offset, Math.min(offset + CHUNK_BYTES, source.size)));
          options.onProgress?.(writer.bytesWritten, total);
        }
      } else if (source instanceof ArrayBuffer || ArrayBuffer.isView(source)) {
        const view =
          source instanceof ArrayBuffer ? new Uint8Array(source) : new Uint8Array(source.buffer, source.byteOffset, source.byteLength);
        for (let offset = 0; offset < view.byteLength; offset += CHUNK_BYTES) {
          if (cancelled()) return await abort();
          await writer.write(view.subarray(offset, Math.min(offset + CHUNK_BYTES, view.byteLength)));
          options.onProgress?.(writer.bytesWritten, total);
        }
      } else {
        const reader = source.getReader();
        try {
          for (;;) {
            if (cancelled()) return await abort();
            const { done, value } = await reader.read();
            if (done) break;
            if (value !== undefined) {
              await writer.write(value);
              options.onProgress?.(writer.bytesWritten, total);
            }
          }
        } finally {
          reader.releaseLock();
        }
      }
      return await writer.close();
    } catch (cause) {
      await writer.abort().catch(() => undefined);
      throw cause;
    }
  }

  async tryGet(id: string): Promise<Blob | undefined> {
    const record = await readIndex(id);
    if (record === undefined) return undefined;
    if (record.backend === 'opfs') {
      if (this.directory === null) return undefined;
      try {
        const handle = await this.directory.getFileHandle(id);
        return await handle.getFile();
      } catch {
        return undefined;
      }
    }
    return readIndexedDBObject(id, record);
  }

  async get(id: string): Promise<Blob> {
    const blob = await this.tryGet(id);
    if (blob === undefined) {
      throw new StorageError(
        'notFound',
        'That sound is no longer stored on this device.',
        'Open the pad and pick a replacement sound, or restore the project from a backup.',
      );
    }
    return blob;
  }

  async getFileURL(id: string): Promise<string> {
    const tracked = objectURLs.get(id);
    if (tracked !== undefined) {
      tracked.refs += 1;
      return tracked.url;
    }
    const blob = await this.get(id);
    const url = URL.createObjectURL(blob);
    objectURLs.set(id, { url, refs: 1 });
    return url;
  }

  releaseFileURL(id: string): void {
    const tracked = objectURLs.get(id);
    if (tracked === undefined) return;
    tracked.refs -= 1;
    if (tracked.refs > 0) return;
    URL.revokeObjectURL(tracked.url);
    objectURLs.delete(id);
  }

  releaseAllFileURLs(): void {
    revokeAll();
  }

  async delete(id: string): Promise<void> {
    assertValidID(id);
    this.forceReleaseURL(id);
    const record = await readIndex(id);
    if (record === undefined) {
      // No index row does not prove no bytes: a crash between the two is exactly the case
      // `reconcile` exists for, so remove both anyway.
      if (this.directory !== null) await this.directory.removeEntry(id).catch(() => undefined);
      await deleteIndexedDBObject(id).catch(() => undefined);
      return;
    }
    if (record.backend === 'opfs' && this.directory !== null) {
      await this.directory.removeEntry(id).catch(() => undefined);
      await writeStore('blobs', (store) => store.delete(id));
      return;
    }
    await deleteIndexedDBObject(id);
  }

  async deleteMatching(prefix: string): Promise<number> {
    const records = await readStore('blobs', (store) => store.getAll(keyRange.prefix(prefix)));
    let reclaimed = 0;
    for (const record of records) {
      reclaimed += record.byteSize;
      await this.delete(record.id);
    }
    return reclaimed;
  }

  async has(id: string): Promise<boolean> {
    return (await readIndex(id)) !== undefined;
  }

  async size(id: string): Promise<number> {
    const record = await readIndex(id);
    return record?.byteSize ?? 0;
  }

  async list(prefix?: string): Promise<BlobStat[]> {
    const records = await readStore('blobs', (store) =>
      store.getAll(prefix === undefined ? undefined : keyRange.prefix(prefix)),
    );
    return records.map((record) => ({
      id: record.id,
      byteSize: record.byteSize,
      mimeType: record.mimeType,
      backend: record.backend,
    }));
  }

  async totalBytes(prefix?: string): Promise<number> {
    const records = await readStore('blobs', (store) =>
      store.getAll(prefix === undefined ? undefined : keyRange.prefix(prefix)),
    );
    let total = 0;
    for (const record of records) total += record.byteSize;
    return total;
  }

  async copy(fromID: string, toID: string): Promise<BlobStat> {
    assertValidID(fromID);
    assertValidID(toID);
    const source = await this.get(fromID);
    const record = await readIndex(fromID);
    return this.put(toID, source, { mimeType: record?.mimeType ?? source.type });
  }

  async reconcile(): Promise<number> {
    const records = await readStore('blobs', (store) => store.getAll());
    const known = new Set(records.map((record) => record.id));
    let reclaimed = 0;

    for (const record of records) {
      const actual = await this.measure(record);
      if (actual === undefined) {
        await writeStore('blobs', (store) => store.delete(record.id));
        continue;
      }
      if (actual !== record.byteSize) {
        await writeIndex({ ...record, byteSize: actual, updatedAt: nowISODate() });
      }
    }

    // Bytes with no owner: the tail of an import that was interrupted after the file was
    // created and before the row was written.
    //
    // "Unowned" is not the same as "abandoned". A file with a writer open right now is the
    // *original* being imported or the take being saved, and a file written seconds ago
    // belongs to a writer this tab cannot see. Both are skipped: reclaiming a few megabytes
    // is never worth deleting audio that is still arriving, and an orphan that is genuinely
    // dead is still there on the next sweep.
    if (this.directory !== null && typeof this.directory.keys === 'function') {
      const orphans: string[] = [];
      try {
        for await (const name of this.directory.keys()) {
          if (known.has(name) || name === PROBE_NAME) continue;
          if (this.writing.has(name)) continue;
          orphans.push(name);
        }
      } catch {
        // Directory iteration is not available everywhere; the index stays authoritative.
      }
      const settled = Date.now() - ORPHAN_GRACE_MS;
      for (const name of orphans) {
        let size = 0;
        try {
          const file = await (await this.directory.getFileHandle(name)).getFile();
          if (file.lastModified > settled) continue;
          size = file.size;
        } catch {
          // Unreadable: nothing can be recovered from it, so it goes.
        }
        await this.directory.removeEntry(name).catch(() => undefined);
        reclaimed += size;
      }
    }

    const chunkKeys = await readStore('blobChunks', (store) => store.getAllKeys());
    const orphanIDs = new Set<string>();
    for (const key of chunkKeys) {
      const id = Array.isArray(key) ? key[0] : key;
      if (typeof id === 'string' && !known.has(id)) orphanIDs.add(id);
    }
    for (const id of orphanIDs) {
      await transact(['blobChunks'], 'readwrite', (tx) => tx.store('blobChunks').delete(keyRange.compoundPrefix(id)));
    }

    if (reclaimed > 0 || orphanIDs.size > 0) {
      log.info('reclaimed unowned audio', { bytes: byteTag(reclaimed), files: orphanIDs.size });
    }
    return reclaimed;
  }

  /** Actual stored size, or `undefined` when the bytes are gone. */
  private async measure(record: BlobRecord): Promise<number | undefined> {
    if (record.backend === 'opfs') {
      if (this.directory === null) return undefined;
      try {
        const handle = await this.directory.getFileHandle(record.id);
        return (await handle.getFile()).size;
      } catch {
        return undefined;
      }
    }
    const chunks = await readStore('blobChunks', (store) => store.getAll(keyRange.compoundPrefix(record.id)));
    if (chunks.length === 0) return record.byteSize === 0 ? 0 : undefined;
    let total = 0;
    for (const chunk of chunks) total += chunk.data.size;
    return total;
  }

  /** Drops every outstanding reference, used when the bytes behind a URL are about to go. */
  private forceReleaseURL(id: string): void {
    const tracked = objectURLs.get(id);
    if (tracked === undefined) return;
    URL.revokeObjectURL(tracked.url);
    objectURLs.delete(id);
  }
}

// MARK: - Entry point

/** Opens the blob store once and hands the same instance to every caller. */
export function getBlobStore(): Promise<BlobStore> {
  storePromise ??= (async (): Promise<BlobStore> => {
    opfsDirectory = await detectOPFS();
    // The database has to be open before the fallback name can be decided: whether writes
    // land in IndexedDB or in memory is settled by whether IndexedDB opened at all.
    await openDatabase();
    const fallback: BlobBackendName = databaseBackend() === 'memory' ? 'memory' : 'indexeddb';
    const store = new PadLabBlobStore(opfsDirectory, fallback);
    log.info('audio storage ready', { opfs: opfsDirectory !== null, backend: idPrefix(store.backend) });
    return store;
  })();
  return storePromise;
}
